import { Button, TextField } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
  getCompanyConfig,
  getOfferDefaultsConfig,
  getOperarioMobileConfig,
  updateCompanyConfig,
  updateOfferDefaultsConfig,
  updateOperarioMobileConfig,
  uploadCompanyLogo,
} from "../../../../api/generalConfig";
import { API } from "../../../../api";
import { alertError, alertSuccess } from "../../../../utils/logger";
import Editor from "../../../components/editor/Editor";
import { useSkeleton } from "../../../hooks/useSkeleton";

function getEmptyOfferDefaults() {
  return {
    id: null,
    cabecera_descripcion: "",
    pie_condiciones_venta: "",
  };
}

function getEmptyCompanyConfig() {
  return {
    nombre_fiscal: "",
    nif: "",
    email_contacto: "",
    telefono: "",
    direccion_postal: "",
    politica_privacidad_html: "",
    url_base_publica: new URL(API, window.location.href).origin,
    logo_url: null,
  };
}

export default function GeneralConfigPage() {
  const [selectedTab, setSelectedTab] = useState("ofertas");
  const [offerDefaults, setOfferDefaults] = useState(getEmptyOfferDefaults());
  const [operarioMobileConfig, setOperarioMobileConfig] = useState({
    id: null,
    url_base_acceso_movil_desarrollo: "",
    url_base_acceso_movil_produccion: "https://field.neo4.es",
  });
  const [companyConfig, setCompanyConfig] = useState(getEmptyCompanyConfig());
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const accessToken = loggedUser?.accessToken;
  const { isLoading, disableLoading, ContentSkeleton } = useSkeleton();

  useEffect(() => {
    Promise.all([getOfferDefaultsConfig(accessToken), getOperarioMobileConfig(accessToken), getCompanyConfig(accessToken)])
      .then(([offerResponse, operariosResponse, companyResponse]) => {
        if (offerResponse.status === 200) {
          setOfferDefaults({ ...getEmptyOfferDefaults(), ...offerResponse.data });
        }
        if (operariosResponse.status === 200) {
          setOperarioMobileConfig({
            id: null,
            url_base_acceso_movil_desarrollo: "",
            url_base_acceso_movil_produccion: "https://field.neo4.es",
            ...operariosResponse.data,
          });
        }
        if (companyResponse.status === 200) {
          setCompanyConfig({
            ...getEmptyCompanyConfig(),
            ...companyResponse.data,
            url_base_publica: companyResponse.data.url_base_publica || getEmptyCompanyConfig().url_base_publica,
          });
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido obtener la configuración general.",
        });
      })
      .finally(disableLoading);
  }, [accessToken, disableLoading]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedTab === "ofertas") {
        await updateOfferDefaultsConfig(offerDefaults, accessToken);
      } else if (selectedTab === "operarios") {
        await updateOperarioMobileConfig(operarioMobileConfig, accessToken);
      } else if (selectedTab === "empresa") {
        const response = await updateCompanyConfig(companyConfig, accessToken);
        setCompanyConfig((current) => ({ ...current, ...response.data }));
      }
      alertSuccess({
        title: "Guardado",
        customMessage:
          selectedTab === "ofertas"
            ? "Los valores por defecto de las ofertas se han guardado."
            : selectedTab === "operarios"
              ? "La URL de acceso móvil de operarios se ha guardado."
              : "La información de empresa se ha guardado.",
      });
    } catch (error) {
      alertError({
        error,
        customMessage: "No se ha podido guardar la configuración general.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024 || !["image/png", "image/jpeg"].includes(file.type)) {
      alertError({ error: "Selecciona un PNG o JPEG de hasta 2 MB." });
      return;
    }
    setUploadingLogo(true);
    try {
      const response = await uploadCompanyLogo(file, accessToken);
      setCompanyConfig((current) => ({ ...current, logo_url: response.data?.logo_url || null }));
      alertSuccess({ customMessage: "Logo actualizado. Su URL pública ya puede usarse en la firma." });
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido subir el logo." });
    } finally {
      setUploadingLogo(false);
    }
  };

  if (isLoading) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title="Configuración general" />
        <CardBody>
          <div className="d-flex border-bottom" style={{ gap: "1.5rem" }}>
          <button
            type="button"
            className="border-0 bg-transparent font-weight-bolder pb-3"
            onClick={() => setSelectedTab("ofertas")}
            style={{ color: selectedTab === "ofertas" ? "#321ACB" : "#7E8299", borderBottom: selectedTab === "ofertas" ? "2px solid #321ACB" : "2px solid transparent" }}
          >
            Ofertas
          </button>
          <button
            type="button"
            className="border-0 bg-transparent font-weight-bolder pb-3"
            onClick={() => setSelectedTab("operarios")}
            style={{ color: selectedTab === "operarios" ? "#321ACB" : "#7E8299", borderBottom: selectedTab === "operarios" ? "2px solid #321ACB" : "2px solid transparent" }}
          >
            Operarios
          </button>
          <button
            type="button"
            className="border-0 bg-transparent font-weight-bolder pb-3"
            onClick={() => setSelectedTab("empresa")}
            style={{ color: selectedTab === "empresa" ? "#321ACB" : "#7E8299", borderBottom: selectedTab === "empresa" ? "2px solid #321ACB" : "2px solid transparent" }}
          >
            Información de empresa
          </button>
          </div>
        </CardBody>
      </Card>

      <Card className="mt-5">
        <CardHeader title={selectedTab === "ofertas" ? "Ofertas" : selectedTab === "operarios" ? "Operarios" : "Información de empresa"}>
          <CardHeaderToolbar>
            <Button color="primary" variant="contained" disabled={saving} onClick={handleSave}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>

        {selectedTab === "ofertas" && (
          <div style={{ maxWidth: "980px" }}>
            <div className="font-weight-bolder text-dark mb-2">Cabecera por defecto</div>
            <div className="text-muted mb-3">Se incluirá en las nuevas ofertas. Podrá editarse individualmente después.</div>
            <Editor
              body={offerDefaults.cabecera_descripcion || ""}
              setBody={(cabecera_descripcion) => setOfferDefaults((current) => ({ ...current, cabecera_descripcion }))}
              placeholder="Texto de cabecera de la oferta"
              lang="es"
            />

            <div className="font-weight-bolder text-dark mt-5 mb-2">Pie y condiciones de venta por defecto</div>
            <div className="text-muted mb-3">Se incluirá en las nuevas ofertas. Podrá editarse individualmente después.</div>
            <Editor
              body={offerDefaults.pie_condiciones_venta || ""}
              setBody={(pie_condiciones_venta) => setOfferDefaults((current) => ({ ...current, pie_condiciones_venta }))}
              placeholder="Condiciones de venta de la oferta"
              lang="es"
            />
          </div>
        )}

        {selectedTab === "operarios" && (
          <div style={{ maxWidth: "760px" }}>
            <div className="font-weight-bolder text-dark mb-2">Acceso móvil de operarios</div>
            <div className="text-muted mb-4">
              Define los destinos de la aplicación de campo. Cada enlace se generará como <code>URL/acceso/token</code> y la API elegirá la URL del entorno donde se esté ejecutando.
            </div>
            <TextField
              label="URL base de desarrollo"
              value={operarioMobileConfig.url_base_acceso_movil_desarrollo || ""}
              onChange={(event) => {
                const url_base_acceso_movil_desarrollo = event.target.value;
                setOperarioMobileConfig((current) => ({ ...current, url_base_acceso_movil_desarrollo }));
              }}
              placeholder="https://movil.tudominio.com"
              helperText="Usa localhost con el puerto de la aplicación de campo. No incluyas /acceso ni el token."
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />
            <TextField
              label="URL base de producción"
              value={operarioMobileConfig.url_base_acceso_movil_produccion || ""}
              onChange={(event) => {
                const url_base_acceso_movil_produccion = event.target.value;
                setOperarioMobileConfig((current) => ({ ...current, url_base_acceso_movil_produccion }));
              }}
              helperText="Destino público de Field."
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />
          </div>
        )}

        {selectedTab === "empresa" && (
          <div style={{ maxWidth: "980px" }}>
            <div className="text-muted mb-4">
              Estos datos se usarán en firmas y documentos nuevos. Los correos y pedidos ya emitidos no se modificarán.
            </div>
            <div className="row">
              {[
                ["nombre_fiscal", "Nombre fiscal"],
                ["nif", "NIF"],
                ["email_contacto", "Email de contacto"],
                ["telefono", "Teléfono"],
              ].map(([field, label]) => (
                <div className="col-md-6" key={field}>
                  <TextField
                    fullWidth margin="normal" variant="outlined" label={label}
                    value={companyConfig[field] || ""}
                    onChange={(event) => {
                      const value = event.target.value;
                      setCompanyConfig((current) => ({ ...current, [field]: value }));
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              ))}
              <div className="col-md-12">
                <TextField
                  fullWidth margin="normal" variant="outlined" label="Dirección postal"
                  value={companyConfig.direccion_postal || ""}
                  onChange={(event) => {
                    const direccion_postal = event.target.value;
                    setCompanyConfig((current) => ({ ...current, direccion_postal }));
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
              <div className="col-md-12">
                <TextField
                  fullWidth margin="normal" variant="outlined" label="Origen público HTTPS de la API"
                  value={companyConfig.url_base_publica || ""}
                  onChange={(event) => {
                    const url_base_publica = event.target.value;
                    setCompanyConfig((current) => ({ ...current, url_base_publica }));
                  }}
                  helperText="Debe ser accesible desde fuera de la red. Se usa para construir la URL pública del logo en los correos. Guarda este campo antes de usar la firma."
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>
            <div className="font-weight-bolder text-dark mt-5 mb-2">Política de privacidad</div>
            <div className="text-muted small mb-3">
              Este aviso se inserta en la firma con letra de 10 px. Puedes editar negritas, tamaños y enlaces desde aquí.
            </div>
            <Editor
              body={companyConfig.politica_privacidad_html || ""}
              setBody={(politica_privacidad_html) => setCompanyConfig((current) => ({ ...current, politica_privacidad_html }))}
              lang="es"
            />
            <div className="font-weight-bolder text-dark mt-5 mb-3">Logo de empresa</div>
            {companyConfig.logo_url && (
              <div className="mb-3">
                <img src={companyConfig.logo_url} alt="Logo de empresa" style={{ maxWidth: "240px", maxHeight: "100px", objectFit: "contain" }} />
                <div className="text-muted small mt-2">{companyConfig.logo_url}</div>
              </div>
            )}
            <Button component="label" variant="outlined" color="primary" disabled={uploadingLogo}>
              {uploadingLogo ? "Subiendo..." : "Subir logo PNG o JPEG"}
              <input type="file" accept="image/png,image/jpeg" hidden onChange={handleLogoUpload} />
            </Button>
            <div className="text-muted small mt-2">Máximo 2 MB. Los logos anteriores se conservan para correos históricos.</div>
          </div>
        )}
        </CardBody>
      </Card>
    </>
  );
}
